import { formatValidQueryParamUrlCommaSeparated } from "utils/common";

import { emptySplitApi } from "./empty-split-api";

// --- Types ---

export type GoogleReviewsOauthConfigResponse = {
  authorization_url: string;
  state: string;
};

export type GoogleReviewsOauthAuthorizeRequest = {
  code: string;
  state: string;
  clinic_id: number;
};

export type GoogleReviewConnection = {
  id: number;
  clinic_id: number;
  clinic_name: string;
  google_account_id: string;
  google_location_id: string;
  location_name: string;
  /** One-line storefront address from Google Business Profile; "" when Google has none, absent on pre-rollout backend payloads. */
  location_address?: string;
  /** Owner-assigned store code from Google Business Profile; "" when unset, absent on pre-rollout backend payloads. */
  store_code?: string;
  is_active: boolean;
  last_sync_at: string | null;
  last_sync_error: string | null;
  consecutive_sync_failures: number;
  created_at: string;
};

export type GoogleReviewPatient = {
  id: number;
  first_name: string;
  last_name: string;
  appointment_date?: string | null;
  provider_name?: string | null;
};

export type GoogleReview = {
  id: number;
  clinic_id: number;
  clinic_name: string;
  connection_id: number;
  /** Google Business Profile location name — distinct per location even when several share a clinic. */
  location_name: string;
  google_review_id: string;
  reviewer_display_name: string;
  reviewer_profile_photo_url: string | null;
  star_rating: number;
  comment: string | null;
  google_created_at: string;
  google_updated_at: string;
  reply_text: string | null;
  reply_at: string | null;
  reply_synced_to_google: boolean;
  reply_sync_error: string | null;
  patient: GoogleReviewPatient | null;
  match_confidence: number | null;
  match_method: string | null;
  created_at: string;
};

// ENG-2595: "SEO" then "Professional" (1-2 star reviews get "Professional" only). A backend from
// before ENG-2595 still answers "Warm" / "Professional" / "Brief"; those render with their own name.
export type ReplySuggestionTone = "SEO" | "Professional" | "Warm" | "Brief";

export type ReplySuggestion = {
  tone: ReplySuggestionTone;
  text: string;
  // SEO only: the practice name, city and service as written in `text`, for highlighting.
  highlights?: string[];
};

export type ReplySuggestionsResponse = {
  suggestions: ReplySuggestion[];
};

export type GoogleReviewsListResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: GoogleReview[];
};

export type GoogleReviewsFilter = {
  clinic_id?: number | number[];
  /** GBP connection (one per Google location) — the per-location filter dimension (SN-19578). */
  connection_id?: number;
  star_rating?: number | number[];
  has_reply?: boolean;
  has_patient?: boolean;
  date_from?: string;
  date_to?: string;
  patient_id?: number;
  /** Free-text search across reviewer name, comment, and reply (DRF SearchFilter). */
  search?: string;
  /** DRF OrderingFilter value, e.g. "-google_created_at" or "needs_reply,-google_created_at". */
  ordering?: string;
  page?: number;
  page_size?: number;
};

export type GoogleReviewSummary = {
  total_reviews: number;
  avg_rating: number;
  unreplied_count: number;
  matched_count: number;
  match_rate: number;
  rating_distribution: Record<string, number>;
  reviews_this_month: number;
  reviews_last_month: number;
};

export type GoogleReviewReplyResponse = {
  reply_text: string;
  reply_at: string;
  reply_synced_to_google: boolean;
};

export type GoogleReviewMatchResponse = {
  patient_id: number;
  match_method: string;
};

export type ReviewsWidgetLayout = "cards" | "carousel";

export type ReviewsWidgetSource = "all" | "handpicked";

/** Practice-level settings for the embeddable Google Reviews website widget (ENG-2320). */
export type ReviewsWidgetSettings = {
  /** Public, non-secret key that identifies the practice in the embed snippet. Read-only. */
  embed_key: string;
  layout: ReviewsWidgetLayout;
  source: ReviewsWidgetSource;
  /** 1–5. Applied server-side: reviews below it never reach a visitor. */
  min_rating: number;
  /** GoogleReview pks, in display order. Max 15. */
  handpicked_review_ids: number[];
  show_book_button: boolean;
  book_url: string;
  show_on_scheduler: boolean;
};

export type ReviewsWidgetSettingsUpdate = Partial<
  Omit<ReviewsWidgetSettings, "embed_key">
>;

// --- API ---

export const googleReviewsApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    // OAuth
    getGoogleReviewsOauthConfig: builder.query<
      GoogleReviewsOauthConfigResponse,
      void
    >({
      query: () => "/google-reviews/oauth/config/"
    }),

    authorizeGoogleReviewsOauth: builder.mutation<
      { connections: GoogleReviewConnection[] },
      GoogleReviewsOauthAuthorizeRequest
    >({
      query: (body) => ({
        url: "/google-reviews/oauth/authorize/",
        method: "POST",
        body
      }),
      invalidatesTags: ["GoogleReviewConnections"]
    }),

    // Connections
    getGoogleReviewConnections: builder.query<GoogleReviewConnection[], void>({
      query: () => "/google-reviews/connections/",
      providesTags: ["GoogleReviewConnections"]
    }),

    deleteGoogleReviewConnection: builder.mutation<void, number>({
      query: (id) => ({
        url: `/google-reviews/connections/${id}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["GoogleReviewConnections"]
    }),

    // Reviews
    getGoogleReviews: builder.query<
      GoogleReviewsListResponse,
      GoogleReviewsFilter
    >({
      query: (filters) => {
        const params = formatValidQueryParamUrlCommaSeparated(filters);
        return `/google-reviews/reviews/?${params}`;
      },
      providesTags: ["GoogleReviews"]
    }),

    getGoogleReview: builder.query<GoogleReview, number>({
      query: (id) => `/google-reviews/reviews/${id}/`,
      providesTags: (_result, _error, id) => [{ type: "GoogleReviews", id }]
    }),

    // Summary
    getGoogleReviewSummary: builder.query<
      GoogleReviewSummary,
      { clinic_id?: number; connection_id?: number } | void
    >({
      query: (params) => {
        const search = new URLSearchParams();
        if (params?.clinic_id)
          search.set("clinic_id", String(params.clinic_id));
        if (params?.connection_id)
          search.set("connection_id", String(params.connection_id));
        const queryString = search.toString();
        return queryString
          ? `/google-reviews/summary/?${queryString}`
          : "/google-reviews/summary/";
      },
      providesTags: ["GoogleReviewSummary"]
    }),

    // Reply
    replyToGoogleReview: builder.mutation<
      GoogleReviewReplyResponse,
      { id: number; text: string }
    >({
      query: ({ id, text }) => ({
        url: `/google-reviews/reviews/${id}/reply/`,
        method: "POST",
        body: { text }
      }),
      invalidatesTags: ["GoogleReviews", "GoogleReviewSummary"]
    }),

    editGoogleReviewReply: builder.mutation<
      GoogleReviewReplyResponse,
      { id: number; text: string }
    >({
      query: ({ id, text }) => ({
        url: `/google-reviews/reviews/${id}/reply/`,
        method: "PUT",
        body: { text }
      }),
      invalidatesTags: ["GoogleReviews"]
    }),

    deleteGoogleReviewReply: builder.mutation<void, number>({
      query: (id) => ({
        url: `/google-reviews/reviews/${id}/reply/`,
        method: "DELETE"
      }),
      invalidatesTags: ["GoogleReviews", "GoogleReviewSummary"]
    }),

    // AI reply suggestions (SN-18749). Mutation, not query: it's a user-initiated,
    // billable, uncached POST that re-fires on Regenerate. No cache tags — suggestions
    // are ephemeral and must never invalidate the list or be served stale.
    getReplySuggestions: builder.mutation<
      ReplySuggestionsResponse,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/google-reviews/reviews/${id}/suggest-replies/`,
        method: "POST",
        body: {},
        timeout: 20000
      })
    }),

    // Patient Match
    matchGoogleReviewPatient: builder.mutation<
      GoogleReviewMatchResponse,
      { id: number; patient_id: number }
    >({
      query: ({ id, patient_id }) => ({
        url: `/google-reviews/reviews/${id}/match/`,
        method: "POST",
        body: { patient_id }
      }),
      invalidatesTags: ["GoogleReviews", "GoogleReviewSummary"]
    }),

    unmatchGoogleReviewPatient: builder.mutation<void, number>({
      query: (id) => ({
        url: `/google-reviews/reviews/${id}/match/`,
        method: "DELETE"
      }),
      invalidatesTags: ["GoogleReviews", "GoogleReviewSummary"]
    }),

    // Website widget settings (ENG-2320). One row per practice; GET creates it on first read.
    getReviewsWidgetSettings: builder.query<ReviewsWidgetSettings, void>({
      query: () => "/google-reviews/widget-settings/",
      providesTags: ["GoogleReviewsWidgetSettings"]
    }),

    updateReviewsWidgetSettings: builder.mutation<
      ReviewsWidgetSettings,
      ReviewsWidgetSettingsUpdate
    >({
      query: (body) => ({
        url: "/google-reviews/widget-settings/",
        method: "PATCH",
        body
      }),
      invalidatesTags: ["GoogleReviewsWidgetSettings"]
    }),

    // Services the SEO-optimized reply suggestion may name (ENG-2595), stored on Practice.review_reply_keywords.
    // A narrow practice read, so the new RestQL field never enters the shared practice query; the
    // section renders its own errors.
    getReplyKeywords: builder.query<string[], number>({
      query: (practiceId) =>
        `/practices/${practiceId}/?query={id,review_reply_keywords}`,
      transformResponse: (response: { review_reply_keywords?: string[] }) =>
        response.review_reply_keywords ?? [],
      providesTags: ["GoogleReviewsReplyKeywords"],
      extraOptions: { suppressGlobalErrorToast: true }
    }),

    updateReplyKeywords: builder.mutation<
      unknown,
      { practiceId: number; keywords: string[] }
    >({
      query: ({ practiceId, keywords }) => ({
        url: `/practices/${practiceId}/`,
        method: "PATCH",
        body: { review_reply_keywords: keywords }
      }),
      invalidatesTags: ["GoogleReviewsReplyKeywords"],
      extraOptions: { suppressGlobalErrorToast: true }
    })
  })
});

export const {
  useGetGoogleReviewsOauthConfigQuery,
  useLazyGetGoogleReviewsOauthConfigQuery,
  useAuthorizeGoogleReviewsOauthMutation,
  useGetGoogleReviewConnectionsQuery,
  useDeleteGoogleReviewConnectionMutation,
  useGetGoogleReviewsQuery,
  useGetGoogleReviewQuery,
  useGetGoogleReviewSummaryQuery,
  useReplyToGoogleReviewMutation,
  useEditGoogleReviewReplyMutation,
  useDeleteGoogleReviewReplyMutation,
  useGetReplySuggestionsMutation,
  useMatchGoogleReviewPatientMutation,
  useUnmatchGoogleReviewPatientMutation,
  useGetReviewsWidgetSettingsQuery,
  useUpdateReviewsWidgetSettingsMutation,
  useGetReplyKeywordsQuery,
  useUpdateReplyKeywordsMutation
} = googleReviewsApi;
